import { combineEpics, Epic, ofType } from "redux-observable";
import { from, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { AuthActions } from "../auth/actions";
import { ChainService } from "../chain/service";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { VaultService } from "../vaults/service";
import { VerificationService } from "../verification/service";
import { BillingActions } from "./actions";
import { BillingDTO } from "./models/dto";
import { BillingService } from "./service";

const getPricing: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getPricing),
    switchMap<
      BillingActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<BillingDTO.Responses.GetPricing.T>
      >
    >(() => BillingService.getPricing()),
    map((x) => BillingActions.setPricing(x))
  );

const getBalance: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getBalance),
    switchMap<
      BillingActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<BillingDTO.Responses.GetBalance.T>
      >
    >(() => BillingService.getBalance()),
    map((x) => BillingActions.setBalance(x))
  );

// const getChainBillingEpic: Epic<BillingActions.Actions> = (action$) =>
//   action$.pipe(
//     ofType(BillingActions.ActionTypes.getChainBilling),
//     switchMap<
//       BillingActions.Actions,
//       Observable<
//         FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
//       >
//     >(() => ChainService.getBilling()),
//     map((x) => BillingActions.setChainBilling(x))
//   );

// const getVaultsBillingEpic: Epic<BillingActions.Actions> = (action$) =>
//   action$.pipe(
//     ofType(BillingActions.ActionTypes.getVaultsBilling),
//     switchMap<
//       BillingActions.Actions,
//       Observable<
//         FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
//       >
//     >(() => VaultService.getBilling()),
//     map((x) => BillingActions.setVaultsBilling(x))
//   );

// const getVerificationBillingEpic: Epic<BillingActions.Actions> = (action$) =>
//   action$.pipe(
//     ofType(BillingActions.ActionTypes.getVerificationBilling),
//     switchMap<
//       BillingActions.Actions,
//       Observable<
//         FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
//       >
//     >(() => VerificationService.getBilling()),
//     map((x) => BillingActions.setVerificationBilling(x))
//   );

const getForexRateEpic: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getForexRate),
    switchMap<
      BillingActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<BillingDTO.Responses.GetForexRate.T>
      >
    >(() => BillingService.getForexRate()),
    map((x) => BillingActions.setForexRate(x))
  );

const getSKRTAddressesEpic: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getSKRTAddresses),
    switchMap<
      BillingActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<BillingDTO.Responses.GetSKRTAddresses.T>
      >
    >(() => BillingService.getSKRTAddresses()),
    map((x) => BillingActions.setSKRTAddresses(x))
  );

const getAllTransactionsEpic: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getAllTransactions),
    switchMap<
      BillingActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<BillingDTO.Responses.GetAllTransactions.T>
      >
    >(() => BillingService.getAllTransactions()),
    map((x) => BillingActions.setAllTransactions(x))
  );

const setAllTransactionsPaginationEpic: Epic<BillingActions.Actions> = (
  action$
) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.setAllTransactionsPagination),
    switchMap<
      ReturnType<typeof BillingActions.setAllTransactionsPagination>,
      Observable<
        | FetchableError
        | FetchableSuccess<BillingDTO.Responses.GetAllTransactions.T>
      >
    >((action) => {
      return BillingService.getAllTransactions(action.payload);
    }),
    map((x) => BillingActions.setAllTransactions(x))
  );

const getAllUsagesEpic: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.getAllUsages),
    switchMap<
      BillingActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<BillingDTO.Responses.GetAllUsages.T>
      >
    >(() => BillingService.getAllUsages()),
    map((x) => BillingActions.setAllUsages(x))
  );

const setAllUsagesPaginationEpic: Epic<BillingActions.Actions> = (action$) =>
  action$.pipe(
    ofType(BillingActions.ActionTypes.setAllUsagesPagination),
    switchMap<
      ReturnType<typeof BillingActions.setAllUsagesPagination>,
      Observable<
        FetchableError | FetchableSuccess<BillingDTO.Responses.GetAllUsages.T>
      >
    >((action) => {
      return BillingService.getAllUsages(action.payload);
    }),
    map((x) => BillingActions.setAllUsages(x))
  );

const dependencies: Epic<
  AuthActions.Actions | BillingActions.Actions,
  BillingActions.Actions
> = (action$) =>
    action$.pipe(
      ofType(AuthActions.ActionTypes.authDone),
      switchMap(() =>
        from([
          BillingActions.resetPricing(),
          BillingActions.resetBalance(),
          BillingActions.resetChainBilling(),
          BillingActions.resetVaultsBilling(),
          BillingActions.resetVerificationBilling(),
          BillingActions.resetSKRTAddresses(),
          BillingActions.resetAllTransactions(),
          BillingActions.resetAllUsages(),
        ])
      )
    );

export const billngEpic = combineEpics(
  dependencies,
  getPricing,
  getBalance,
  // getChainBillingEpic,
  // getVaultsBillingEpic,
  // getVerificationBillingEpic,
  getForexRateEpic,
  getSKRTAddressesEpic,
  getAllTransactionsEpic,
  setAllTransactionsPaginationEpic,
  getAllUsagesEpic,
  setAllUsagesPaginationEpic
);
